import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { QrCode, Microphone, VideoCamera, UploadSimple, Eye, ArrowLeft } from '@phosphor-icons/react';
import { EventDropNav, EventDropLogo } from '../components/EventDropLogo';
import { useEventRecovery, useEventSession } from '../context/EventSessionProvider';
import ReconnectingScreen from '../components/ReconnectingScreen';
import BackButton from '../components/BackButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRESET_BG = {
  wedding: { background: `linear-gradient(180deg, rgba(244,114,182,0.3) 0%, rgba(251,191,36,0.15) 50%, rgba(120,53,15,0.4) 100%), #2D1B2E` },
  party: { background: `linear-gradient(135deg, #0F0723, #1A0A3E, #0D0D0D)` },
  corporate: { background: 'linear-gradient(180deg, #F1F5F9, #E2E8F0)' },
};

const EventPreview = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { event: recoveredEvent, status: recoveryStatus, retry: retryRecovery } = useEventRecovery(slug);
  const { activeEvent } = useEventSession();
  const [event, setEvent] = useState(null);
  const [config, setConfig] = useState(null);
  const [activeTab, setActiveTab] = useState('upload');

  useEffect(() => {
    if (recoveryStatus !== 'ready' || !recoveredEvent?.event_id) return;
    setEvent(recoveredEvent);
    axios.get(`${API}/events/${recoveredEvent.event_id}/screen-config`)
      .then((c) => setConfig(c.data))
      .catch(() => {});
  }, [recoveryStatus, recoveredEvent]);

  if (recoveryStatus === 'failed') return <ReconnectingScreen status="failed" onRetry={retryRecovery} dashboardHref="/dashboard" slug={slug} activeEvent={activeEvent} />;
  if (!event) return <ReconnectingScreen status={recoveryStatus === 'restoring' ? 'restoring' : 'reconnecting'} slug={slug} activeEvent={activeEvent} />;

  const uploadUrl = `${window.location.origin}/event/${slug}`;
  const guestbookUrl = `${window.location.origin}/event/${slug}/guestbook`;
  const bgStyle = config?.bg_type === 'color' ? { background: config.bg_value } : PRESET_BG[config?.bg_value] || PRESET_BG.wedding;

  return (
    <div className="min-h-screen bg-[#15161B] text-white" style={{ fontFamily: "'Inter', sans-serif" }} data-testid="event-preview-page">
      {/* Header */}
      <div className="max-w-4xl mx-auto px-6 py-6">
        <div className="flex items-center justify-between mb-8">
          <BackButton fallbackHref={`/event/${slug}`} label="Back" variant="subtle" testId="preview-back" />
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20">
            <Eye size={14} weight="bold" className="text-amber-400" />
            <span className="text-amber-400 text-xs font-bold">PREVIEW MODE</span>
          </div>
        </div>

        <div className="text-center mb-8">
          <EventDropLogo size={40} className="mx-auto mb-3" />
          <h1 className="text-3xl font-black tracking-tight mb-2" data-testid="preview-event-name">{event.event_name}</h1>
          <p className="text-gray-500">{event.event_date}</p>
          <p className="text-gray-600 text-sm mt-2">Preview what your guests will see</p>
        </div>

        {/* Tab Selector */}
        <div className="flex justify-center gap-2 mb-8" data-testid="preview-tabs">
          {[
            { id: 'upload', label: 'QR Upload', icon: <UploadSimple size={16} weight="bold" /> },
            { id: 'guestbook', label: 'Guestbook', icon: <Microphone size={16} weight="bold" /> },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${activeTab === tab.id ? 'bg-white text-gray-900' : 'bg-white/10 text-gray-400 hover:text-white'}`}
              data-testid={`preview-tab-${tab.id}`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Preview Content */}
        <div className="flex justify-center">
          {/* Device frame */}
          <div className="relative" style={{ width: 380, maxWidth: '100%' }}>
            <div className="rounded-[32px] bg-[#15161B] p-3" style={{ boxShadow: '0 25px 60px rgba(0,0,0,0.4)' }}>
              <div className="rounded-[22px] overflow-hidden bg-black" style={{ minHeight: 560 }}>

                {/* QR Upload Preview */}
                {activeTab === 'upload' && (
                  <div className="p-6 flex flex-col items-center justify-center" style={{ minHeight: 560, background: 'linear-gradient(180deg, #15161B, #1a1020)' }} data-testid="preview-upload-screen">
                    <EventDropLogo size={48} className="mb-4" />
                    <h3 className="text-xl font-bold text-white mb-2">{event.event_name}</h3>
                    <p className="text-gray-400 text-sm mb-6 text-center">Share your photos and videos</p>
                    <div className="w-40 h-40 bg-white rounded-2xl p-3 mb-6 flex items-center justify-center">
                      <QrCode size={120} weight="bold" className="text-gray-900" />
                    </div>
                    <div className="w-full max-w-xs">
                      <div className="w-full py-3 rounded-xl text-center font-bold text-white text-sm" style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}>
                        Upload Photo or Video
                      </div>
                    </div>
                    <p className="text-gray-600 text-xs mt-4">Guests scan to upload instantly</p>
                  </div>
                )}

                {/* Guestbook Preview */}
                {activeTab === 'guestbook' && (
                  <div className="flex flex-col items-center justify-center px-6" style={{ minHeight: 560, ...bgStyle }} data-testid="preview-guestbook-screen">
                    <div className="absolute inset-0 bg-black/20 rounded-[22px]" />
                    <div className="relative z-10 flex flex-col items-center">
                      <h3 className="text-2xl font-bold text-white mb-6 text-center" style={{ fontFamily: "'Playfair Display', serif", textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
                        {config?.title || 'Leave a Message'}
                      </h3>
                      <div className="flex gap-4">
                        <div className="w-24 h-24 rounded-2xl flex flex-col items-center justify-center" style={{ background: config?.btn_audio_color || '#F28B82' }}>
                          <Microphone size={32} weight="bold" className="text-white" />
                          <span className="text-white text-[10px] font-semibold mt-1">{config?.btn_audio_label || 'Record Audio'}</span>
                        </div>
                        <div className="w-24 h-24 rounded-2xl flex flex-col items-center justify-center" style={{ background: config?.btn_video_color || '#26A69A' }}>
                          <VideoCamera size={32} weight="bold" className="text-white" />
                          <span className="text-white text-[10px] font-semibold mt-1">{config?.btn_video_label || 'Record Video'}</span>
                        </div>
                      </div>
                      <p className="text-white/70 text-xs mt-5 text-center">{config?.subtitle || 'Tap to leave a 30 second message!'}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Share Links */}
        <div className="mt-10 text-center space-y-3" data-testid="preview-links">
          <p className="text-gray-500 text-sm mb-4">Share these links with your venue team</p>
          <div className="flex flex-col gap-2 max-w-md mx-auto">
            <button onClick={() => { navigator.clipboard.writeText(uploadUrl); }} className="flex items-center justify-between px-4 py-3 rounded-xl bg-[#15161B] border border-white/10 hover:border-[#C29D52]/30 transition-colors" data-testid="copy-upload-link">
              <span className="text-sm text-gray-300">QR Upload Page</span>
              <span className="text-xs text-[#B89762] font-semibold">Copy Link</span>
            </button>
            <button onClick={() => { navigator.clipboard.writeText(guestbookUrl); }} className="flex items-center justify-between px-4 py-3 rounded-xl bg-[#15161B] border border-white/10 hover:border-teal-500/30 transition-colors" data-testid="copy-guestbook-link">
              <span className="text-sm text-gray-300">Guestbook Screen</span>
              <span className="text-xs text-teal-400 font-semibold">Copy Link</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventPreview;
